/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2020 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property of
 * Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S.
 * and Foreign Patents, patent applications, and are protected by trade secret
 * or copyright law. Dissemination of this information, reproduction of this
 * material, and copying or distribution of this software is strictly forbidden
 * unless prior written permission is obtained from Pearson Education, Inc.
 */

/* eslint-disable no-param-reassign */

import { types } from 'mobx-state-tree';
import { getBulkExportStatusByUser } from '../services/bulkExportService';
import {
  bulkActionFieldNames,
  EXPORT_STATUS
} from '../constants/bulkActionStatusConstants';
import { toExportTitleCase } from '../utils/bulkActionUtils';

// Helper function to determine status from a bulk request
function determineStatusFromRequest(request) {
  let rawStatus = EXPORT_STATUS.QUEUED;

  if (!request) rawStatus = EXPORT_STATUS.QUEUED;
  else if (
    request.statuses.every(
      status => status.toLowerCase() === EXPORT_STATUS.COMPLETE.toLowerCase()
    )
  ) {
    rawStatus = EXPORT_STATUS.COMPLETE;
  } else if (
    request.statuses.some(
      status => status.toLowerCase() === EXPORT_STATUS.QUEUED.toLowerCase()
    ) &&
    request.statuses.some(
      status => status.toLowerCase() === EXPORT_STATUS.IN_PROGRESS.toLowerCase()
    ) &&
    request.statuses.some(
      status => status.toLowerCase() === EXPORT_STATUS.COMPLETE.toLowerCase()
    ) &&
    request.statuses.some(
      status => status.toLowerCase() === EXPORT_STATUS.ERROR.toLowerCase()
    ) &&
    request.statuses.length === 4
  ) {
    rawStatus = EXPORT_STATUS.IN_PROGRESS;
  } else if (
    request.statuses.some(
      status => status.toLowerCase() === EXPORT_STATUS.QUEUED.toLowerCase()
    ) &&
    request.statuses.some(
      status => status.toLowerCase() === EXPORT_STATUS.IN_PROGRESS.toLowerCase()
    ) &&
    request.statuses.some(
      status => status.toLowerCase() === EXPORT_STATUS.ERROR.toLowerCase()
    ) &&
    request.statuses.length === 3
  ) {
    rawStatus = EXPORT_STATUS.IN_PROGRESS;
  } else if (
    request.statuses.some(
      status => status.toLowerCase() === EXPORT_STATUS.COMPLETE.toLowerCase()
    ) &&
    request.statuses.some(
      status => status.toLowerCase() === EXPORT_STATUS.ERROR.toLowerCase()
    ) &&
    request.statuses.some(
      status => status.toLowerCase() === EXPORT_STATUS.QUEUED.toLowerCase()
    ) &&
    request.statuses.length === 3
  ) {
    rawStatus = EXPORT_STATUS.IN_PROGRESS;
  } else if (
    request.statuses.some(
      status => status.toLowerCase() === EXPORT_STATUS.COMPLETE.toLowerCase()
    ) &&
    request.statuses.some(
      status => status.toLowerCase() === EXPORT_STATUS.ERROR.toLowerCase()
    ) &&
    request.statuses.some(
      status => status.toLowerCase() === EXPORT_STATUS.IN_PROGRESS.toLowerCase()
    ) &&
    request.statuses.length === 3
  ) {
    rawStatus = EXPORT_STATUS.IN_PROGRESS;
  }
  // Check for exactly the combination of complete and error, nothing else
  else if (
    request.statuses.some(
      status => status.toLowerCase() === EXPORT_STATUS.COMPLETE.toLowerCase()
    ) &&
    request.statuses.some(
      status => status.toLowerCase() === EXPORT_STATUS.ERROR.toLowerCase()
    ) &&
    request.statuses.length === 2
  ) {
    rawStatus = EXPORT_STATUS.COMPLETE;
  } else if (
    request.statuses.some(
      status => status.toLowerCase() === EXPORT_STATUS.IN_PROGRESS.toLowerCase()
    ) &&
    request.statuses.some(
      status => status.toLowerCase() === EXPORT_STATUS.ERROR.toLowerCase()
    ) &&
    request.statuses.length === 2
  ) {
    rawStatus = EXPORT_STATUS.IN_PROGRESS;
  } else if (
    request.statuses.some(
      status => status.toLowerCase() === EXPORT_STATUS.QUEUED.toLowerCase()
    ) &&
    request.statuses.some(
      status => status.toLowerCase() === EXPORT_STATUS.ERROR.toLowerCase()
    ) &&
    request.statuses.length === 2
  ) {
    rawStatus = EXPORT_STATUS.IN_PROGRESS;
  } else if (
    request.statuses.some(
      status => status.toLowerCase() === EXPORT_STATUS.ERROR.toLowerCase()
    )
  ) {
    rawStatus = EXPORT_STATUS.ERROR;
  } else {
    // Default to queued if no other conditions match
    rawStatus = EXPORT_STATUS.QUEUED;
  }

  return toExportTitleCase(rawStatus);
}

// Helper function to calculate progress percentage
function calculateProgressFromRequest(request) {
  if (
    !request ||
    !request.statusCounts ||
    !Array.isArray(request.statusCounts)
  ) {
    return 0;
  }

  const total = request.count || 0;
  if (total === 0) return 0;
  const completedCount =
    request.statusCounts.find(
      s => s.status.toLowerCase() === EXPORT_STATUS.COMPLETE.toLowerCase()
    )?.count || 0;
  const errorCount =
    request.statusCounts.find(
      s => s.status.toLowerCase() === EXPORT_STATUS.ERROR.toLowerCase()
    )?.count || 0;

  return Math.round(((completedCount + errorCount) / total) * 100);
}

function getCompletedCountFromRequest(statusCounts) {
  if (!Array.isArray(statusCounts)) return 0; // Get completed and error counts separately
  const completedCount =
    statusCounts.find(
      s => s.status.toLowerCase() === EXPORT_STATUS.COMPLETE.toLowerCase()
    )?.count || 0;
  const errorCount =
    statusCounts.find(
      s => s.status.toLowerCase() === EXPORT_STATUS.ERROR.toLowerCase()
    )?.count || 0;

  // Return the sum of completed and error counts
  return completedCount + errorCount;
}

// Define the export status item model
const BulkExport = types.model('BulkExport', {
  id: types.optional(types.identifier, () =>
    Math.random().toString(36).substring(2, 9)
  ),
  operationType: types.optional(types.string, 'PDF'),
  title: types.maybeNull(types.string),
  userid: types.maybeNull(types.string),
  exportInitiateTime: types.maybeNull(types.string),
  startTime: types.maybeNull(types.string),
  endTime: types.maybeNull(types.string),
  errorDetails: types.maybeNull(types.string),
  status: types.maybeNull(types.string),
  progress: types.optional(types.number, 0),
  total: types.optional(types.number, 0),
  completed: types.optional(types.number, 0),
  bulkRequestId: types.maybeNull(types.string),
  fileLocation: types.maybeNull(types.string),
  statuses: types.optional(types.array(types.string), [])
});

export default types
  .model('BulkExport', {
    exportData: types.optional(types.array(BulkExport), []),

    hasMore: types.maybeNull(types.boolean, false),
    totalCount: types.maybeNull(types.number, 0),
    sortBy: types.optional(
      types.string,
      bulkActionFieldNames.EXPORT_INITIATE_TIME
    ),
    sortDirection: types.optional(types.enumeration(['asc', 'desc']), 'desc'),
    page: types.optional(types.number, 1),
    pageSize: types.optional(types.number, 10),
    isLoading: types.optional(types.boolean, false)
  })
  .views(self => ({
    get sortedExportData() {
      const sortableData = [...self.exportData];

      if (self.sortBy) {
        sortableData.sort((a, b) => {
          if (a[self.sortBy] < b[self.sortBy]) {
            return self.sortDirection === 'asc' ? -1 : 1;
          }
          if (a[self.sortBy] > b[self.sortBy]) {
            return self.sortDirection === 'asc' ? 1 : -1;
          }
          return 0;
        });
      }

      return sortableData;
    }
  }))
  .actions(self => ({
    resetBulkProjects() {
      self.exportData = [];
      self.totalCount = 0;
      self.hasMore = false;
    },

    setIsLoading(isLoading) {
      self.isLoading = isLoading;
    },

    resetBulkPageIndex() {
      self.page = 1;
    },

    resetList() {
      self.hasMore = false;
      self.totalCount = 0;
    },
    setBulkExportList(bulkRequests) {
      self.exportData = bulkRequests;
    },
    setHasMore(hasMore) {
      self.hasMore = hasMore;
    },
    setTotalCount(totalCount) {
      self.total = totalCount;
      // self.totalCount = totalCount;
    },
    setNewPageValue(page) {
      self.page = page;
    },
    setStatusCounts(statusCounts) {
      self.statusCounts = statusCounts;
    },

    setProgress(progress) {
      self.progress = progress;
    },

    setSorting(field) {
      if (self.sortBy === field) {
        // Toggle direction if same field
        self.sortDirection = self.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        // New field, default to ascending
        self.sortBy = field;
        self.sortDirection = 'asc';
      }
    },

    addBulkRequests(bulkRequests) {
      // Process each bulk request and add it as a properly formatted ExportData item
      const newItems = [];

      bulkRequests.forEach(request => {
        // Define default values for fields
        const status = determineStatusFromRequest(request);
        const progress = calculateProgressFromRequest(request);
        newItems.push({
          id: Math.random().toString(36).substring(2, 9),
          operationType: request.exportType || 'PDF',
          title: request.title || '',
          userid: request.exportByUser || request.userid || '',
          exportInitiateTime: request.exportInitiateTime || '',
          startTime: request.startTime || '',
          endTime: request.endTime || '',
          errorDetails: '',
          status,
          progress,
          total: request.count || 0,
          completed: getCompletedCountFromRequest(request.statusCounts),
          bulkRequestId: request.bulkRequestId,
          statuses: request.statuses || []
        });
      });

      // Use update to modify the array in one action
      self.exportData = [...self.exportData, ...newItems];
    },

    async loadBulkExportProjects(params, resetProjects) {
      const apiParams = {
        page: resetProjects ? 1 : self.page,
        pageSize: self.pageSize,
        ...params
      };
      self.setIsLoading(true);

      if (resetProjects) {
        self.resetBulkPageIndex();
        self.resetBulkProjects();
      }

      const exportStatusData = await getBulkExportStatusByUser(apiParams);
      const {
        bulkRequests = [],
        hasMore = false
        // totalCount = 0
      } = exportStatusData;
      // Update the export data with the new bulk requests
      self.addBulkRequests(bulkRequests);

      self.setHasMore(hasMore);
      self.setIsLoading(false);
    },

    async updateBulkExportProjects() {
      const updatedPageValue = self.page + 1;
      self.setNewPageValue(updatedPageValue);
      const apiParams = {
        page: updatedPageValue || self.page,
        pageSize: self.pageSize
      };
      self.setIsLoading(true);
      const exportStatusData = await getBulkExportStatusByUser(apiParams);
      const {
        bulkRequests = [],
        hasMore = false
        // totalCount = 0
      } = exportStatusData;

      self.addBulkRequests(bulkRequests);

      self.setHasMore(hasMore);
      self.setIsLoading(false);
    }
  }));
